import { UserAction } from "@brookbush/types";

import { useAuth } from "@clerk/nextjs";
import type { PortableTextBlock } from "@portabletext/types";
import clsx from "clsx";
import Image from "next/image";

import TextBlock from "~/components/TextBlock";
import { useUserAction } from "~/hooks";
import useTrackCTA from "~/hooks/useTrackCTA";

interface HeroProps {
	title?: string;
	subtitle?: PortableTextBlock[];
	imageUrl?: string;
	link?: {
		href: string;
		label: string;
	};
}

const TrialHero = ({ title, subtitle, imageUrl }: HeroProps) => {
	const { isSignedIn } = useAuth();
	const { setUserAction } = useUserAction();
	const trackCTA = useTrackCTA();

	return (
		<section
			className={clsx(
				"relative flex w-full flex-col items-center justify-center gap-8 px-8 pt-8 pb-10 text-center sm:gap-10 lg:h-[calc(90vh-4rem)]",
			)}
			aria-label="Hero"
		>
			<div className="absolute -top-16 h-[calc(100%+4rem)] w-[100vw] overflow-hidden brightness-[0.4] lg:h-[90vh]">
				<Image
					src={
						imageUrl ||
						"https://vczc4mkjnoo13s0s.public.blob.vercel-storage.com/sign-up-bg.jpeg"
					}
					fill
					priority
					loading="eager"
					alt="background image"
					className="object-cover"
				/>
			</div>
			{title ? (
				<h1 className="z-10 max-w-6xl font-semibold text-4xl text-white tracking-tight sm:text-5xl lg:text-7xl">
					{title}
				</h1>
			) : (
				<h1 className="z-10 max-w-6xl font-semibold text-4xl text-white tracking-tight sm:text-5xl lg:text-7xl">
					Get a{" "}
					<span className="relative text-sky-400 sm:whitespace-nowrap">
						<svg
							aria-hidden="true"
							viewBox="0 0 418 42"
							className="absolute top-2/3 left-0 h-[0.58em] w-full fill-sky-300/70"
							preserveAspectRatio="none"
						>
							<path d="M203.371.916c-26.013-2.078-76.686 1.963-124.73 9.946L67.3 12.749C35.421 18.062 18.2 21.766 6.004 25.934 1.244 27.561.828 27.778.874 28.61c.07 1.214.828 1.121 9.595-1.176 9.072-2.377 17.15-3.92 39.246-7.496C123.565 7.986 157.869 4.492 195.942 5.046c7.461.108 19.25 1.696 19.17 2.582-.107 1.183-7.874 4.31-25.75 10.366-21.992 7.45-35.43 12.534-36.701 13.884-2.173 2.308-.202 4.407 4.442 4.734 2.654.187 3.263.157 15.593-.78 35.401-2.686 57.944-3.488 88.365-3.143 46.327.526 75.721 2.23 130.788 7.584 19.787 1.924 20.814 1.98 24.557 1.332l.066-.011c1.201-.203 1.53-1.825.399-2.335-2.911-1.31-4.893-1.604-22.048-3.261-57.509-5.556-87.871-7.36-132.059-7.842-23.239-.254-33.617-.116-50.627.674-11.629.54-42.371 2.494-46.696 2.967-2.359.259 8.133-3.625 26.504-9.81 23.239-7.825 27.934-10.149 28.304-14.005.417-4.348-3.529-6-16.878-7.066Z" />
						</svg>
						<span className="relative">Free 7 Day Trial</span>
					</span>{" "}
					to the Brookbush Institute
				</h1>
			)}

			{subtitle ? (
				<TextBlock
					value={subtitle}
					className="dark-text-block dark !text-xl md:!text-2xl z-10 max-w-4xl"
				/>
			) : (
				<p className="z-10 hidden max-w-5xl text-3xl text-white lg:block">
					3 Certifications, 170+ Courses, 500+ Videos and so much more. <br />
					<i>
						&apos;It&apos;s Amazing... Like Netflix for certifications and
						courses!&apos;
					</i>
				</p>
			)}

			<button
				onClick={() => {
					if (!isSignedIn) {
						trackCTA(UserAction.SIGN_UP, "Trial Page Hero");
						setUserAction(UserAction.SIGN_UP);
					} else {
						trackCTA(UserAction.BECOME_A_MEMBER, "Trial Page Hero");
						setUserAction(UserAction.BECOME_A_MEMBER);
					}
				}}
				className="group z-10 flex items-center gap-2 rounded-xl bg-gradient-to-r from-sky-500 to-sky-700 px-4 py-2 text-2xl text-white shadow-xl hover:brightness-95 lg:px-6 lg:py-3"
			>
				Get Free Trial
			</button>
		</section>
	);
};

export default TrialHero;
